/**
 * Voice AI — Pipeline Settings
 * Global defaults for STT sensitivity, barge-in, TTS speed, LLM tuning.
 * Per-agent overrides are managed in agents-manager.js.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class PipelineSettings extends LitElement {
  static properties = {
    _config: { state: true },
    _saving: { state: true },
    _loaded: { state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    settingsStyles,
    css`
      .slider-row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .slider-row input[type="range"] {
        flex: 1;
        -webkit-appearance: none;
        height: 6px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        outline: none;
      }

      .slider-row input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        cursor: pointer;
        box-shadow: var(--shadow-sm);
        transition: all var(--transition-fast);
      }

      .slider-row input[type="range"]::-webkit-slider-thumb:hover {
        transform: scale(1.15);
        box-shadow: var(--shadow-glow-primary);
      }

      .slider-value-badge {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-primary);
        background: var(--color-primary-light);
        padding: 2px var(--space-2);
        border-radius: var(--radius-sm);
        min-width: 52px;
        text-align: center;
        flex-shrink: 0;
      }

      .number-input {
        width: 80px;
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        text-align: center;
      }

      .number-input:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }
    `
  ];

  constructor() {
    super();
    this._config = {
      voice_pipeline_mode: 'standard',
      voice_realtime_voice: 'coral',
      voice_realtime_vad_mode: 'server_vad',
      voice_pipeline_stt_endpointing: 600,
      voice_pipeline_stt_utterance_end_ms: 1000,
      voice_pipeline_bargein_mode: 'allow',
      voice_pipeline_bargein_min_words: 2,
      voice_pipeline_tts_speed: 1.0,
      voice_pipeline_llm_temperature: 0.7,
      voice_pipeline_llm_max_tokens: 300,
      voice_pipeline_max_history: 40,
      voice_pipeline_sentence_min_length: 10,
      voice_pipeline_max_tool_retries: 3,
      voice_pipeline_min_tool_pause_ms: 3000,
      voice_pipeline_silence_timeout_ms: 20000,
      voice_pipeline_silence_max_prompts: 3,
      voice_pipeline_silence_escalation: 1.5,
      voice_pipeline_disconnect_ttl_hours: 48,
    };
    this._saving = false;
    this._loaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const data = await settingsService.loadPipelineConfig();
      if (data.success && data.config) {
        this._config = { ...this._config, ...data.config };
        this._loaded = true;
      }
    } catch (err) {
      console.error('[PipelineSettings] Load failed:', err);
    }
  }

  _set(key, value) {
    this._config = { ...this._config, [key]: value };
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;
    try {
      const data = await settingsService.savePipelineConfig(this._config);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.message || 'Pipeline settings saved', type: 'success' },
          bubbles: true, composed: true
        }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.error || 'Save failed', type: 'error' },
          bubbles: true, composed: true
        }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', {
        detail: { message: 'Network error saving pipeline settings', type: 'error' },
        bubbles: true, composed: true
      }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    const c = this._config;
    return html`
      ${iconStyles}

      <div class="content-header">
        <h2 class="content-title">Pipeline Settings</h2>
        <p class="content-description">Fine-tune how the voice pipeline processes speech, generates responses, and handles interruptions. These are system-wide defaults — individual agents can override them.</p>
      </div>

      <!-- Pipeline Mode -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-route section-icon"></i>
          <span class="section-title">Pipeline Mode</span>
        </div>
        <p class="section-description">Choose how calls are processed. Standard uses separate STT + LLM + TTS providers. Realtime uses OpenAI's end-to-end speech model — lower latency but requires an OpenAI LLM provider with a realtime model.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Mode</label>
            <select class="form-select"
              .value=${c.voice_pipeline_mode}
              @change=${e => this._set('voice_pipeline_mode', e.target.value)}
            >
              <option value="standard">Standard (STT + LLM + TTS)</option>
              <option value="realtime">Realtime (OpenAI end-to-end)</option>
            </select>
            <span class="form-hint">Individual agents can override this in their Pipeline tab.</span>
          </div>

          ${c.voice_pipeline_mode === 'realtime' ? html`
            <div class="form-group">
              <label class="form-label">Realtime Voice</label>
              <select class="form-select"
                .value=${c.voice_realtime_voice}
                @change=${e => this._set('voice_realtime_voice', e.target.value)}
              >
                <option value="alloy">Alloy</option>
                <option value="ash">Ash</option>
                <option value="ballad">Ballad</option>
                <option value="coral">Coral</option>
                <option value="echo">Echo</option>
                <option value="sage">Sage</option>
                <option value="shimmer">Shimmer</option>
                <option value="verse">Verse</option>
                <option value="marin">Marin</option>
                <option value="cedar">Cedar</option>
              </select>
              <span class="form-hint">OpenAI voice for the realtime pipeline.</span>
            </div>

            <div class="form-group">
              <label class="form-label">VAD Mode</label>
              <select class="form-select"
                .value=${c.voice_realtime_vad_mode}
                @change=${e => this._set('voice_realtime_vad_mode', e.target.value)}
              >
                <option value="server_vad">Server VAD — Automatic turn detection</option>
                <option value="semantic_vad">Semantic VAD — AI-driven turn detection</option>
              </select>
              <span class="form-hint">Server VAD uses silence-based detection. Semantic VAD uses the model to determine when the caller is done speaking (higher quality, slightly more latency).</span>
            </div>

            <div class="form-group" style="grid-column:1/-1;">
              <div style="background:var(--color-bg-inset);border:1px solid var(--color-border-primary);border-radius:var(--radius-md);padding:var(--space-3) var(--space-4);display:flex;align-items:center;gap:var(--space-3);">
                <i class="fa-sharp fa-light fa-circle-info" style="color:var(--color-primary);font-size:var(--text-lg);"></i>
                <span style="font-size:var(--text-sm);color:var(--color-text-secondary);">STT and TTS provider settings below are ignored in Realtime mode — OpenAI handles speech recognition and synthesis internally.</span>
              </div>
            </div>
          ` : ''}
        </div>
      </div>

      <!-- STT Tuning -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-waveform-lines section-icon"></i>
          <span class="section-title">Speech Recognition (STT)</span>
        </div>
        <p class="section-description">Control how the system detects when the caller has finished speaking.</p>

        <div class="form-grid full">
          <div class="form-group">
            <label class="form-label">Endpointing</label>
            <div class="slider-row">
              <input type="range" min="200" max="1500" step="50"
                .value=${String(c.voice_pipeline_stt_endpointing)}
                @input=${e => this._set('voice_pipeline_stt_endpointing', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${c.voice_pipeline_stt_endpointing}ms</span>
            </div>
            <span class="form-hint">Silence duration before a speech segment is considered final. Lower = faster responses but may cut off pauses. Higher = more patient but slower.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Utterance End Timeout</label>
            <div class="slider-row">
              <input type="range" min="1000" max="5000" step="100"
                .value=${String(c.voice_pipeline_stt_utterance_end_ms)}
                @input=${e => this._set('voice_pipeline_stt_utterance_end_ms', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${c.voice_pipeline_stt_utterance_end_ms}ms</span>
            </div>
            <span class="form-hint">Total silence before the full utterance is emitted and sent to the AI. Controls how long to wait for the caller to continue speaking.</span>
          </div>
        </div>
      </div>

      <!-- Interruption Control -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-hand section-icon"></i>
          <span class="section-title">Interruption Control</span>
        </div>
        <p class="section-description">How the agent handles the caller speaking while the AI is talking.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Barge-in Mode</label>
            <select class="form-select"
              .value=${c.voice_pipeline_bargein_mode}
              @change=${e => this._set('voice_pipeline_bargein_mode', e.target.value)}
            >
              <option value="allow">Allow — Stop and listen when caller speaks</option>
              <option value="mute">Mute — Ignore caller audio while AI speaks</option>
              <option value="disabled">Disabled — Never interrupt AI speech</option>
            </select>
            <span class="form-hint">"Allow" provides the most natural conversation. "Mute" prevents echo issues. "Disabled" forces the AI to finish before listening.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Min Words to Interrupt</label>
            <input type="number" class="number-input" min="1" max="10"
              .value=${String(c.voice_pipeline_bargein_min_words)}
              @change=${e => this._set('voice_pipeline_bargein_min_words', Math.max(1, Math.min(10, parseInt(e.target.value) || 2)))}
            />
            <span class="form-hint">Minimum words the caller must say to trigger a barge-in. Prevents coughs or background noise from interrupting.</span>
          </div>
        </div>
      </div>

      <!-- Response Tuning -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-sliders section-icon"></i>
          <span class="section-title">Response Tuning</span>
        </div>
        <p class="section-description">Control the speed, creativity, and length of AI responses.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">TTS Speed</label>
            <div class="slider-row">
              <input type="range" min="0.5" max="2.0" step="0.1"
                .value=${String(c.voice_pipeline_tts_speed)}
                @input=${e => this._set('voice_pipeline_tts_speed', parseFloat(e.target.value))}
              />
              <span class="slider-value-badge">${Number(c.voice_pipeline_tts_speed).toFixed(1)}x</span>
            </div>
            <span class="form-hint">Speech synthesis speed. 1.0 is normal, 1.2 is slightly faster, 0.8 is slower and more deliberate.</span>
          </div>

          <div class="form-group">
            <label class="form-label">LLM Temperature</label>
            <div class="slider-row">
              <input type="range" min="0.0" max="1.5" step="0.1"
                .value=${String(c.voice_pipeline_llm_temperature)}
                @input=${e => this._set('voice_pipeline_llm_temperature', parseFloat(e.target.value))}
              />
              <span class="slider-value-badge">${Number(c.voice_pipeline_llm_temperature).toFixed(1)}</span>
            </div>
            <span class="form-hint">Controls randomness/creativity. Lower = more focused and predictable. Higher = more varied and creative.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Max Response Tokens</label>
            <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2);">
              <div
                class="toggle ${c.voice_pipeline_llm_max_tokens === 0 ? 'active' : ''}"
                @click=${() => this._set('voice_pipeline_llm_max_tokens', c.voice_pipeline_llm_max_tokens === 0 ? 300 : 0)}
              ></div>
              <span style="font-size:var(--text-sm);color:var(--color-text-secondary);">No Max</span>
            </div>
            ${c.voice_pipeline_llm_max_tokens > 0 ? html`
              <div class="slider-row">
                <input type="range" min="100" max="10000" step="100"
                  .value=${String(c.voice_pipeline_llm_max_tokens)}
                  @input=${e => this._set('voice_pipeline_llm_max_tokens', parseInt(e.target.value))}
                />
                <span class="slider-value-badge">${c.voice_pipeline_llm_max_tokens.toLocaleString()}</span>
              </div>
            ` : html`
              <span class="form-hint" style="color:var(--color-primary);font-weight:var(--font-medium);">Provider default — no cap on response length.</span>
            `}
            <span class="form-hint">Maximum length of AI responses. Lower values produce shorter, faster replies.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Conversation Memory</label>
            <input type="number" class="number-input" min="10" max="100" step="5"
              .value=${String(c.voice_pipeline_max_history)}
              @change=${e => this._set('voice_pipeline_max_history', Math.max(10, Math.min(100, parseInt(e.target.value) || 40)))}
            />
            <span class="form-hint">Messages kept in conversation history. Higher values give more context but use more tokens.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Min Sentence Length</label>
            <input type="number" class="number-input" min="0" max="50"
              .value=${String(c.voice_pipeline_sentence_min_length)}
              @change=${e => this._set('voice_pipeline_sentence_min_length', Math.max(0, Math.min(50, parseInt(e.target.value) || 10)))}
            />
            <span class="form-hint">Minimum characters before a sentence fragment is sent to TTS. Prevents tiny audio clips.</span>
          </div>
        </div>
      </div>

      <!-- Tool Execution -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-wrench section-icon"></i>
          <span class="section-title">Tool Execution</span>
        </div>
        <p class="section-description">Control how the AI handles tool calls (availability checks, reservations, payments, etc.).</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Max Tool Retries</label>
            <div class="slider-row">
              <input type="range" min="1" max="10" step="1"
                .value=${String(c.voice_pipeline_max_tool_retries)}
                @input=${e => this._set('voice_pipeline_max_tool_retries', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${c.voice_pipeline_max_tool_retries}x</span>
            </div>
            <span class="form-hint">Maximum times the AI can retry a failing tool before giving up and telling the caller. Prevents infinite retry loops that waste call time.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Min Tool Pause</label>
            <div class="slider-row">
              <input type="range" min="0" max="8000" step="500"
                .value=${String(c.voice_pipeline_min_tool_pause_ms)}
                @input=${e => this._set('voice_pipeline_min_tool_pause_ms', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${(c.voice_pipeline_min_tool_pause_ms / 1000).toFixed(1)}s</span>
            </div>
            <span class="form-hint">Minimum time between the AI saying "let me check" and delivering the answer. Prevents instant answers that sound unhuman. Set to 0 to disable.</span>
          </div>
        </div>
      </div>

      <!-- Silence Detection -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp fa-light fa-volume-slash section-icon"></i>
          <span class="section-title">Silence Detection</span>
        </div>
        <p class="section-description">When the caller goes silent, the agent will prompt them — escalating through stages before disconnecting. Like a real receptionist checking if you're still there.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Initial Timeout</label>
            <div class="slider-row">
              <input type="range" min="3000" max="30000" step="1000"
                .value=${String(c.voice_pipeline_silence_timeout_ms)}
                @input=${e => this._set('voice_pipeline_silence_timeout_ms', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${(c.voice_pipeline_silence_timeout_ms / 1000).toFixed(0)}s</span>
            </div>
            <span class="form-hint">Seconds of silence before the first "Are you still there?" prompt.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Max Prompts Before Disconnect</label>
            <div class="slider-row">
              <input type="range" min="1" max="5" step="1"
                .value=${String(c.voice_pipeline_silence_max_prompts)}
                @input=${e => this._set('voice_pipeline_silence_max_prompts', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${c.voice_pipeline_silence_max_prompts}</span>
            </div>
            <span class="form-hint">How many times the agent asks if the caller is still there before hanging up.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Escalation Factor</label>
            <div class="slider-row">
              <input type="range" min="1.0" max="3.0" step="0.1"
                .value=${String(c.voice_pipeline_silence_escalation)}
                @input=${e => this._set('voice_pipeline_silence_escalation', parseFloat(e.target.value))}
              />
              <span class="slider-value-badge">${Number(c.voice_pipeline_silence_escalation).toFixed(1)}x</span>
            </div>
            <span class="form-hint">Each subsequent silence timeout is multiplied by this factor. Higher = more patience between prompts.</span>
          </div>

          <div class="form-group">
            <label class="form-label">Disconnect Memory TTL</label>
            <div class="slider-row">
              <input type="range" min="1" max="168" step="1"
                .value=${String(c.voice_pipeline_disconnect_ttl_hours)}
                @input=${e => this._set('voice_pipeline_disconnect_ttl_hours', parseInt(e.target.value))}
              />
              <span class="slider-value-badge">${c.voice_pipeline_disconnect_ttl_hours}h</span>
            </div>
            <span class="form-hint">When a call is disconnected prematurely, the agent remembers for this many hours. On callback within this window, the agent will acknowledge the disconnect and offer to resume.</span>
          </div>
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }
}

customElements.define('pipeline-settings', PipelineSettings);
